import { cn } from '@Pages/pageRuntime/lib/utils';
import { OTPInput, OTPInputContext, REGEXP_ONLY_DIGITS } from 'input-otp';
import * as React from 'react';

type InputOTPProps = React.ComponentProps<typeof OTPInput>;

const InputOTP = ({ className, containerClassName, ...props }: InputOTPProps) => (
  <OTPInput
    data-slot="input-otp"
    containerClassName={cn('flex items-center gap-2 has-disabled:opacity-50', containerClassName)}
    className={cn('disabled:cursor-not-allowed', className)}
    {...props}
  />
);

const InputOTPGroup = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div data-slot="input-otp-group" className={cn('flex items-center', className)} {...props} />
);

const InputOTPSlot = ({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & { index: number }) => {
  const slot = React.useContext(OTPInputContext).slots[index];

  return (
    <div
      data-slot="input-otp-slot"
      data-active={slot?.isActive ? '' : undefined}
      className={cn(
        'relative flex h-9 w-9 items-center justify-center border-y border-r border-input text-sm shadow-xs transition-all first:rounded-l-md first:border-l last:rounded-r-md',
        slot?.isActive && 'z-10 ring-1 ring-ring',
        className
      )}
      {...props}
    >
      {slot?.char}
      {slot?.hasFakeCaret ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-pulse bg-foreground" />
        </div>
      ) : null}
    </div>
  );
};

export type CodeInputProps = Omit<
  InputOTPProps,
  'maxLength' | 'value' | 'onChange' | 'onComplete' | 'render' | 'children' | 'pattern'
> & {
  value?: string;
  onChange?: (value: string) => void;
  /** Number of digits. */
  length?: number;
  /** Called with the full code once every cell holds a digit. */
  onComplete?: (code: string) => void;
};

/**
 * A one-time code input: one cell per digit over a single hidden input, so paste,
 * backspace, and phone keyboard autofill from an incoming text all work.
 */
const CodeInput = ({
  value,
  onChange,
  length = 6,
  onComplete,
  containerClassName,
  ref,
  ...props
}: CodeInputProps & { ref?: React.Ref<HTMLInputElement> }) => (
  <InputOTP
    ref={ref}
    maxLength={length}
    value={value}
    onChange={onChange}
    onComplete={onComplete}
    pattern={REGEXP_ONLY_DIGITS}
    inputMode="numeric"
    autoComplete="one-time-code"
    containerClassName={containerClassName}
    {...props}
  >
    {/* The cells show the code as text, so session replays must not record it. */}
    <InputOTPGroup data-sentry-mask>
      {Array.from({ length }, (_, index) => (
        <InputOTPSlot key={index} index={index} />
      ))}
    </InputOTPGroup>
  </InputOTP>
);
CodeInput.displayName = 'CodeInput';

export { CodeInput, InputOTP, InputOTPGroup, InputOTPSlot };
